import { c as _c } from "react/compiler-runtime";
import AddChatIcon from '@precis-digital/kurama/icons/AddChatIcon';
import Button from '@precis-digital/kurama/Button';
import CommentIcon from '@precis-digital/kurama/icons/CommentIcon';
import DotsLoader from '@precis-digital/kurama/DotsLoader';
import Dropdown, { DropdownItem, DropdownList, DropdownListSubCategory } from '@precis-digital/kurama/Dropdown';
import Grid from '@precis-digital/kurama/Grid';
import HistoryIcon from '@precis-digital/kurama/icons/HistoryIcon';
import Link from '@precis-digital/kurama/Link';
import MessageLayout, { MessageComposer, MessageList, type MessageComposerHandle } from '@precis-digital/kurama/MessageLayout';
import { keyframes } from '@precis-digital/kurama/theme';
import { styled } from '@precis-digital/kurama/utils';
import Typography from '@precis-digital/kurama/Typography';
import { useEffect, useMemo, useState, type ReactElement, type Ref } from 'react';
import { useChangePath, useRouteParams } from 'shared/components/Router';
import { useCopilot } from 'shared/context/CopilotContext';
import { format, formatDateToRelativeFormat } from 'shared/dateFns';
import { useCopilotSurface } from 'shared/copilot/CopilotSurfaceContext';
import { getFullscreenChatIdFromRouteParams } from 'shared/copilot/utils/fullscreenChatRoute';
import { APP_ROUTES } from 'shared/routes';
import { useTranslation } from 'shared/translations';
import { useCreateMessagesFromAgUiMessages } from '../hooks/useCreateMessagesFromAgUiMessages';
import { useGetChatHistoryGroupedByDate } from '../hooks/useGetChatHistoryGroupedByDate';
import { buildCopilotAiSubtleAmbientGlow } from '../utils/copilotElementHighlight';
import { useCopilotComposerSkills } from '../hooks/useCopilotComposerSkills';
import { type CopilotComposerSubmit } from '../types';
import { CopilotComposerAttachmentAction } from './CopilotComposerAttachmentAction';
import { CopilotComposerAttachments } from './CopilotComposerAttachments';

/** Keep in sync with Kurama MessageLayout transition duration. */
const CENTERED_LAYOUT_TRANSITION_MS = 320;
const AMBIENT_GLOW_FADE_IN_MS = 900;
const ALVIE_TRUST_CENTER_URL = 'https://alvie.io/legal/trust-center';
const ambientGlowFadeExpand = keyframes({
  from: {
    opacity: 0,
    transform: 'translate(-50%, -50%) scale(0.72)'
  },
  to: {
    opacity: 1,
    transform: 'translate(-50%, -50%) scale(1)'
  }
});
interface CopilotChatShellProps {
  emptyState: ReactElement;
  messageControls: ReactElement;
  inputPlaceholder: string;
  /** Placeholder used instead of `inputPlaceholder` when skills are enabled and the `/` menu has skills. */
  skillsInputPlaceholder?: string;
  onSubmit: CopilotComposerSubmit;
  emptyComposerPlacement?: 'docked' | 'centered';
  composerRef?: Ref<MessageComposerHandle>;
  /** Composer action buttons. Pass `[]` to hide attachment/skills (drawer). */
  composerActions?: ReactElement[];
  /** Files pasted into the composer. Leave unset where the surface has no attachments (drawer): pasting then inserts text only. */
  onComposerPasteFiles?: (files: File[]) => void;
  /**
   * Enables skills: catalog fetch, `/` slash commands, the default skills action,
   * and skill tags in outbound messages. Independent of `composerActions`.
   */
  enableSkills?: boolean;
  /** Optional label anchored to the composer bottom-right (e.g. Alvie AI Pro badge). */
  composerCornerLabel?: string;
  /**
   * Sticky content above the composer divider on empty chats (e.g. suggested prompts).
   * Hidden once the conversation has messages.
   */
  emptyComposerTopAccessory?: ReactElement | null;
  /**
   * Sync MessageList scroll to Frame topnav shadow (standalone fullscreen only).
   * Leave unset/false for the drawer so other pages are unaffected.
   */
  syncFrameScrollShadow?: boolean;
  /**
   * Top inset (px) above the message list. Standalone uses Frame content
   * padding so pinned messages sit below the topnav; the inset is scrollable
   * so history can move through the gap instead of clipping early.
   */
  messageListOffsetTop?: number;
}

/**
 * Shared Copilot chat chrome (message list, composer, privacy).
 * Surfaces compose their own empty state, controls, and submit behaviour.
 */
export const CopilotChatShell = t0 => {
  const $ = _c(67);
  const {
    emptyState,
    messageControls,
    inputPlaceholder,
    skillsInputPlaceholder,
    onSubmit,
    emptyComposerPlacement: t1,
    composerRef,
    composerActions: composerActionsProp,
    onComposerPasteFiles,
    enableSkills: t2,
    composerCornerLabel,
    emptyComposerTopAccessory,
    syncFrameScrollShadow: t3,
    messageListOffsetTop: t4
  } = t0;
  const emptyComposerPlacement = t1 === undefined ? "docked" : t1;
  const enableSkills = t2 === undefined ? false : t2;
  const syncFrameScrollShadow = t3 === undefined ? false : t3;
  const messageListOffsetTop = t4 === undefined ? 0 : t4;
  const {
    messages,
    isLoading,
    isHydratingChat,
    chatLoadFailed,
    retryChatLoad,
    stopGeneration,
    isCsvUploading,
    isImageUploading,
    selectedEntityTypes,
    currentChatId
  } = useCopilot();
  const surface = useCopilotSurface();
  const routeParams = useRouteParams();
  let t5;
  if ($[0] !== routeParams) {
    t5 = getFullscreenChatIdFromRouteParams(routeParams);
    $[0] = routeParams;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const routeChatId = t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      keyPrefix: "copilot"
    };
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const {
    t
  } = useTranslation("common", t6);
  const t7 = composerRef != null && typeof composerRef !== "function" ? composerRef : undefined;
  let t8;
  if ($[3] !== enableSkills || $[4] !== inputPlaceholder || $[5] !== onSubmit || $[6] !== selectedEntityTypes || $[7] !== skillsInputPlaceholder || $[8] !== t7) {
    t8 = {
      enableSkills,
      inputPlaceholder,
      skillsInputPlaceholder,
      selectedEntityTypes,
      composerRef: t7,
      onSubmit
    };
    $[3] = enableSkills;
    $[4] = inputPlaceholder;
    $[5] = onSubmit;
    $[6] = selectedEntityTypes;
    $[7] = skillsInputPlaceholder;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const {
    inputPlaceholder: composerPlaceholder,
    slashCommands,
    slashCommandsEmptyMessage,
    skillsAction,
    handleSubmit: handleComposerSubmit,
    handleBeforeSubmit: handleBeforeComposerSubmit
  } = useCopilotComposerSkills(t8);
  const createMessagesFromAgUiMessages = useCreateMessagesFromAgUiMessages();
  let t9;
  if ($[10] !== createMessagesFromAgUiMessages || $[11] !== messages) {
    t9 = createMessagesFromAgUiMessages(messages);
    $[10] = createMessagesFromAgUiMessages;
    $[11] = messages;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const messagesWithThinkingSignatures = t9;
  const isLoadingExistingChat = isHydratingChat || surface === "fullscreen" && routeChatId != null && routeChatId !== currentChatId;
  const showChatLoadError = chatLoadFailed && !isLoadingExistingChat;
  const showNewChatEmpty = messagesWithThinkingSignatures.length === 0 && !isLoadingExistingChat && !showChatLoadError;
  let t10;
  if ($[13] !== emptyState || $[14] !== isLoadingExistingChat || $[15] !== retryChatLoad || $[16] !== showChatLoadError || $[17] !== showNewChatEmpty || $[18] !== t) {
    t10 = isLoadingExistingChat ? <StyledChatHydrationLoader><DotsLoader /></StyledChatHydrationLoader> : showChatLoadError ? <StyledChatHydrationLoader flexDirection="column" gap="12px"><Typography variant="body2" color="secondary">{t("chatLoadFailed")}</Typography><Button size="small" variant="outlined" onClick={retryChatLoad}>{t("chatLoadRetry")}</Button></StyledChatHydrationLoader> : showNewChatEmpty ? emptyState : undefined;
    $[13] = emptyState;
    $[14] = isLoadingExistingChat;
    $[15] = retryChatLoad;
    $[16] = showChatLoadError;
    $[17] = showNewChatEmpty;
    $[18] = t;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const listEmptyState = t10;
  const hidePrivacyWhileCenteredEmpty = emptyComposerPlacement === "centered";
  const showComposerAmbientGlow = hidePrivacyWhileCenteredEmpty && showNewChatEmpty;
  const privacyInLayout = !hidePrivacyWhileCenteredEmpty || !showNewChatEmpty;
  let t11;
  if ($[20] !== hidePrivacyWhileCenteredEmpty) {
    t11 = () => !hidePrivacyWhileCenteredEmpty;
    $[20] = hidePrivacyWhileCenteredEmpty;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const [privacyVisible, setPrivacyVisible] = useState(t11);
  let t12;
  let t13;
  if ($[22] !== hidePrivacyWhileCenteredEmpty || $[23] !== showNewChatEmpty) {
    t12 = () => {
      if (!hidePrivacyWhileCenteredEmpty) {
        setPrivacyVisible(true);
        return;
      }
      if (showNewChatEmpty) {
        setPrivacyVisible(false);
        return;
      }
      setPrivacyVisible(false);
      const timeoutId = window.setTimeout(() => {
        setPrivacyVisible(true);
      }, CENTERED_LAYOUT_TRANSITION_MS);
      return () => {
        window.clearTimeout(timeoutId);
      };
    };
    t13 = [hidePrivacyWhileCenteredEmpty, showNewChatEmpty];
    $[22] = hidePrivacyWhileCenteredEmpty;
    $[23] = showNewChatEmpty;
    $[24] = t12;
    $[25] = t13;
  } else {
    t12 = $[24];
    t13 = $[25];
  }
  useEffect(t12, t13);
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <CopilotComposerAttachmentAction key="copilot-composer-attachment" />;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== skillsAction) {
    t15 = [t14, ...(skillsAction != null ? [skillsAction] : [])];
    $[27] = skillsAction;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const defaultComposerActions = t15;
  const composerActions = composerActionsProp ?? defaultComposerActions;
  let t16;
  if ($[29] !== privacyInLayout || $[30] !== privacyVisible || $[31] !== t) {
    t16 = privacyInLayout ? <StyledTypographyPrivacy variant="body3" isVisible={privacyVisible}>{t("privacyDisclaimer")}<br /><Link href={ALVIE_TRUST_CENTER_URL} target="_blank" rel="noopener noreferrer" variant="body3" color="grey">{t("privacyLink")}</Link></StyledTypographyPrivacy> : null;
    $[29] = privacyInLayout;
    $[30] = privacyVisible;
    $[31] = t;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const privacyFooter = t16;
  const t17 = isLoading || isCsvUploading || isImageUploading || isLoadingExistingChat || showChatLoadError;
  let t18;
  if ($[33] !== composerActions || $[34] !== composerPlaceholder || $[35] !== composerRef || $[36] !== handleBeforeComposerSubmit || $[37] !== handleComposerSubmit || $[38] !== isLoading || $[39] !== onComposerPasteFiles || $[40] !== showComposerAmbientGlow || $[41] !== slashCommands || $[42] !== slashCommandsEmptyMessage || $[43] !== stopGeneration || $[44] !== t17) {
    t18 = <MessageComposer ref={composerRef} inputPlaceholder={composerPlaceholder} onSubmit={handleComposerSubmit} onBeforeSubmit={handleBeforeComposerSubmit} onCancel={stopGeneration} isSubmitting={isLoading} actions={composerActions} slashCommands={slashCommands} slashCommandsEmptyMessage={slashCommandsEmptyMessage} elevated={showComposerAmbientGlow} attachmentSlot={_temp} disableSubmit={t17} onPasteFiles={onComposerPasteFiles} />;
    $[33] = composerActions;
    $[34] = composerPlaceholder;
    $[35] = composerRef;
    $[36] = handleBeforeComposerSubmit;
    $[37] = handleComposerSubmit;
    $[38] = isLoading;
    $[39] = onComposerPasteFiles;
    $[40] = showComposerAmbientGlow;
    $[41] = slashCommands;
    $[42] = slashCommandsEmptyMessage;
    $[43] = stopGeneration;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== composerCornerLabel) {
    t19 = composerCornerLabel != null && composerCornerLabel.length > 0 ? <StyledComposerCornerLabel variant="h5" color="secondary">{composerCornerLabel}</StyledComposerCornerLabel> : null;
    $[46] = composerCornerLabel;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t18 || $[49] !== t19) {
    t20 = <StyledComposerShell>{t18}{t19}</StyledComposerShell>;
    $[48] = t18;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  const messageComposer = t20;
  const t21 = showNewChatEmpty ? emptyComposerTopAccessory : undefined;
  let t22;
  if ($[51] !== isLoading || $[52] !== listEmptyState || $[53] !== messagesWithThinkingSignatures) {
    t22 = <MessageList messages={messagesWithThinkingSignatures} isLoading={isLoading} emptyState={listEmptyState} pinLatestUserMessageToTop={true} />;
    $[51] = isLoading;
    $[52] = listEmptyState;
    $[53] = messagesWithThinkingSignatures;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== messageComposer || $[56] !== showComposerAmbientGlow) {
    t23 = showComposerAmbientGlow ? <StyledComposerAmbientGlow>{messageComposer}</StyledComposerAmbientGlow> : messageComposer;
    $[55] = messageComposer;
    $[56] = showComposerAmbientGlow;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== emptyComposerPlacement || $[59] !== messageControls || $[60] !== messageListOffsetTop || $[61] !== privacyFooter || $[62] !== syncFrameScrollShadow || $[63] !== t21 || $[64] !== t22 || $[65] !== t23) {
    t24 = <StyledGridContentContainer><MessageLayout maxWidth="768px" emptyComposerPlacement={emptyComposerPlacement} syncFrameScrollShadow={syncFrameScrollShadow} messageListOffsetTop={messageListOffsetTop} footer={privacyFooter} composerTopAccessory={t21} MessageList={t22} MessageControls={messageControls} MessageComposer={t23} /></StyledGridContentContainer>;
    $[58] = emptyComposerPlacement;
    $[59] = messageControls;
    $[60] = messageListOffsetTop;
    $[61] = privacyFooter;
    $[62] = syncFrameScrollShadow;
    $[63] = t21;
    $[64] = t22;
    $[65] = t23;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  return t24;
};
export const useCopilotChatHistoryControls = () => {
  const $ = _c(26);
  const {
    createNewChat,
    loadChat
  } = useCopilot();
  const surface = useCopilotSurface();
  const {
    changePath
  } = useChangePath();
  const routeParams = useRouteParams();
  let t0;
  if ($[0] !== routeParams) {
    t0 = getFullscreenChatIdFromRouteParams(routeParams);
    $[0] = routeParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const routeChatId = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "copilot"
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    t
  } = useTranslation("common", t1);
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  const isHistoryOpen = dropdownAnchorEl != null;
  let t2;
  if ($[3] !== isHistoryOpen) {
    t2 = {
      enabled: isHistoryOpen
    };
    $[3] = isHistoryOpen;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: chatHistoryGroupedByDate,
    isLoading: isHistoryLoading,
    isError: isHistoryError
  } = useGetChatHistoryGroupedByDate(t2);
  let t3;
  if ($[5] !== t) {
    t3 = (key, mostRecentDate) => {
      if (key === "TODAY") {
        return formatDateToRelativeFormat(mostRecentDate);
      }
      if (key === "YESTERDAY") {
        return t("chatHistoryBuckets.yesterday");
      }
      if (key === "PREVIOUS_7_DAYS") {
        return t("chatHistoryBuckets.previous7Days");
      }
      if (key === "PREVIOUS_30_DAYS") {
        return t("chatHistoryBuckets.previous30Days");
      }
      const [, ym] = key.split(":");
      return format(new Date(`${ym}-01T00:00:00`), "MMMM yyyy");
    };
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const resolveBucketLabel = t3;
  let t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event => {
      setDropdownAnchorEl(event.currentTarget);
    };
    t5 = () => {
      setDropdownAnchorEl(null);
    };
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== changePath || $[10] !== createNewChat || $[11] !== routeChatId || $[12] !== surface) {
    t6 = () => {
      if (surface === "fullscreen") {
        if (routeChatId != null) {
          changePath(APP_ROUTES.copilot.chatPage, undefined, true);
          return;
        }
      }
      createNewChat();
    };
    $[9] = changePath;
    $[10] = createNewChat;
    $[11] = routeChatId;
    $[12] = surface;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== changePath || $[15] !== loadChat || $[16] !== surface) {
    t7 = chatId => {
      if (surface === "fullscreen") {
        changePath(APP_ROUTES.copilot.chatDetailPage({
          chatId
        }), undefined, true);
      } else {
        loadChat(chatId);
      }
      setDropdownAnchorEl(null);
    };
    $[14] = changePath;
    $[15] = loadChat;
    $[16] = surface;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== chatHistoryGroupedByDate || $[19] !== dropdownAnchorEl || $[20] !== isHistoryError || $[21] !== isHistoryLoading || $[22] !== resolveBucketLabel || $[23] !== t6 || $[24] !== t7) {
    t8 = {
      dropdownAnchorEl,
      chatHistoryGroupedByDate,
      isHistoryLoading,
      isHistoryError,
      handleClickHistory: t4,
      handleCloseHistoryPopup: t5,
      handleClickNewChat: t6,
      handleSelectChat: t7,
      resolveBucketLabel
    };
    $[18] = chatHistoryGroupedByDate;
    $[19] = dropdownAnchorEl;
    $[20] = isHistoryError;
    $[21] = isHistoryLoading;
    $[22] = resolveBucketLabel;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
};
export const CopilotChatHistoryDropdown = t0 => {
  const $ = _c(45);
  const {
    dropdownAnchorEl,
    chatHistoryGroupedByDate,
    isLoading,
    isError,
    onClose,
    onSelectChat,
    resolveBucketLabel
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "copilot"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("common", t1);
  let T0;
  let T1;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== chatHistoryGroupedByDate || $[2] !== dropdownAnchorEl || $[3] !== isError || $[4] !== isLoading || $[5] !== onClose || $[6] !== onSelectChat || $[7] !== resolveBucketLabel || $[8] !== t) {
    const historyEntries = Object.entries(chatHistoryGroupedByDate);
    const hasHistory = historyEntries.length > 0;
    T1 = Dropdown;
    t8 = dropdownAnchorEl;
    t9 = dropdownAnchorEl != null;
    t10 = onClose;
    T0 = DropdownList;
    t2 = true;
    if ($[20] !== t) {
      t3 = t("chatHistoryHeader");
      $[20] = t;
      $[21] = t3;
    } else {
      t3 = $[21];
    }
    t4 = isLoading;
    if ($[22] !== isError || $[23] !== isLoading || $[24] !== t) {
      t5 = !isLoading && isError ? <StyledHistoryStatusRow><Typography variant="body2">{t("chatHistoryError")}</Typography></StyledHistoryStatusRow> : null;
      $[22] = isError;
      $[23] = isLoading;
      $[24] = t;
      $[25] = t5;
    } else {
      t5 = $[25];
    }
    if ($[26] !== hasHistory || $[27] !== isError || $[28] !== isLoading || $[29] !== t) {
      t6 = !isLoading && !isError && !hasHistory ? <StyledHistoryStatusRow><Typography variant="body2">{t("chatHistoryEmpty")}</Typography></StyledHistoryStatusRow> : null;
      $[26] = hasHistory;
      $[27] = isError;
      $[28] = isLoading;
      $[29] = t;
      $[30] = t6;
    } else {
      t6 = $[30];
    }
    t7 = !isLoading && !isError ? historyEntries.map(t11 => {
      const [key, entries] = t11;
      return <DropdownListSubCategory key={key} header={resolveBucketLabel(key, entries[0].updatedAt)}>{entries.map(entry => <DropdownItem key={entry.id} leftIcon={<CommentIcon />} onClick={() => {
          onSelectChat(entry.id);
        }}><Typography>{entry.title}</Typography></DropdownItem>)}</DropdownListSubCategory>;
    }) : null;
    $[1] = chatHistoryGroupedByDate;
    $[2] = dropdownAnchorEl;
    $[3] = isError;
    $[4] = isLoading;
    $[5] = onClose;
    $[6] = onSelectChat;
    $[7] = resolveBucketLabel;
    $[8] = t;
    $[9] = T0;
    $[10] = T1;
    $[11] = t10;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    T0 = $[9];
    T1 = $[10];
    t10 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t11;
  if ($[31] !== T0 || $[32] !== t2 || $[33] !== t3 || $[34] !== t4 || $[35] !== t5 || $[36] !== t6 || $[37] !== t7) {
    t11 = <T0 dense={t2} header={t3} isLoading={t4}>{t5}{t6}{t7}</T0>;
    $[31] = T0;
    $[32] = t2;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== T1 || $[40] !== t10 || $[41] !== t11 || $[42] !== t8 || $[43] !== t9) {
    t12 = <T1 anchorEl={t8} open={t9} onClose={t10}>{t11}</T1>;
    $[39] = T1;
    $[40] = t10;
    $[41] = t11;
    $[42] = t8;
    $[43] = t9;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  return t12;
};
export const CopilotChatHistoryActionButtons = t0 => {
  const $ = _c(20);
  const {
    onNewChat,
    onHistoryClick
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "copilot"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("common", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AddChatIcon />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t) {
    t3 = t("newChatTooltip");
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = {
      title: t3,
      kind: "singleline"
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== onNewChat || $[7] !== t4) {
    t5 = <Button size="small" variant="text" leftIcon={t2} onClick={onNewChat} tooltipProps={t4} />;
    $[6] = onNewChat;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <HistoryIcon />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t) {
    t7 = t("chatHistoryTooltip");
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = {
      title: t7,
      kind: "singleline"
    };
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== onHistoryClick || $[15] !== t8) {
    t9 = <Button size="small" variant="text" leftIcon={t6} onClick={onHistoryClick} tooltipProps={t8} />;
    $[14] = onHistoryClick;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t5 || $[18] !== t9) {
    t10 = <Grid display="flex" flexDirection="row" alignItems="center" gap="8px">{t5}{t9}</Grid>;
    $[17] = t5;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
};
const StyledHistoryStatusRow = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  minWidth: '240px'
}));
const StyledGridContentContainer = styled(Grid)(() => ({
  flex: 1,
  minHeight: 0,
  height: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0px',
  width: '100%',
  '& > div:first-of-type': {
    flex: 1,
    minHeight: 0,
    width: '100%'
  }
}));
const StyledComposerAmbientGlow = styled(Grid)(({
  theme
}) => ({
  position: 'relative',
  width: '100%',
  isolation: 'isolate',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '50%',
    top: '62%',
    transform: 'translate(-50%, -50%)',
    width: '150%',
    height: '340%',
    borderRadius: '50%',
    background: buildCopilotAiSubtleAmbientGlow(theme),
    filter: 'blur(64px)',
    pointerEvents: 'none',
    zIndex: 0,
    animation: `${ambientGlowFadeExpand} ${AMBIENT_GLOW_FADE_IN_MS}ms ease-out both`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none'
    }
  },
  '& > *': {
    position: 'relative',
    zIndex: 1
  }
}));
const StyledTypographyPrivacy = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isVisible'
})<{
  isVisible: boolean;
}>(({
  theme,
  isVisible
}) => ({
  textAlign: 'center',
  color: theme.tokens.foreground.main.secondary,
  flexShrink: 0,
  opacity: isVisible ? 1 : 0,
  pointerEvents: isVisible ? 'auto' : 'none',
  transition: `opacity ${CENTERED_LAYOUT_TRANSITION_MS}ms ease-out`,
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none'
  }
}));
const StyledComposerShell = styled(Grid)(() => ({
  position: 'relative',
  width: '100%'
}));
const StyledChatHydrationLoader = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  padding: theme.spacing(4)
}));
const StyledComposerCornerLabel = styled(Typography)(({
  theme
}) => ({
  position: 'absolute',
  right: theme.spacing(4),
  bottom: theme.spacing(2),
  margin: 0,
  fontStyle: 'italic',
  pointerEvents: 'none',
  userSelect: 'none'
}));
function _temp() {
  return <CopilotComposerAttachments />;
}